import React, { useRef, useCallback } from "react";
import { cn } from "../../../lib/utils";
import { highlightCedar } from "../utils/gatewayUtils";

interface GatewayCedarEditorProps {
  value: string;
  onChange: (value: string) => void;
  readOnly?: boolean;
  className?: string;
}

export default function GatewayCedarEditor({ value, onChange, readOnly = false, className }: GatewayCedarEditorProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const highlightRef = useRef<HTMLPreElement>(null);

  const lines = (value || "").split("\n");
  const lineCount = lines.length;

  const handleScroll = useCallback(() => {
    if (textareaRef.current && highlightRef.current) {
      highlightRef.current.scrollTop = textareaRef.current.scrollTop;
      highlightRef.current.scrollLeft = textareaRef.current.scrollLeft;
    }
  }, []);

  return (
    <div className={cn("relative rounded-lg border border-border bg-background/80 overflow-hidden", className)}>
      <div className="flex h-full min-h-[200px] max-h-[500px]">
        <div className="flex flex-col items-end px-3 py-3 text-xs font-mono text-muted-foreground/70 bg-card select-none border-r border-border shrink-0">
          {Array.from({ length: lineCount }, (_, i) => (
            <div key={i} className="leading-5">{i + 1}</div>
          ))}
        </div>

        <div className="relative flex-1 overflow-hidden">
          <pre
            ref={highlightRef}
            className="absolute inset-0 p-3 text-xs font-mono leading-5 whitespace-pre-wrap break-words overflow-auto pointer-events-none"
            aria-hidden
            dangerouslySetInnerHTML={{ __html: highlightCedar(value || "") }}
          />

          <textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onScroll={handleScroll}
            readOnly={readOnly}
            spellCheck={false}
            className="absolute inset-0 w-full h-full p-3 text-xs font-mono leading-5 bg-transparent text-transparent caret-primary resize-none outline-none"
            style={{ caretColor: "hsl(var(--primary))" }}
          />
        </div>
      </div>
    </div>
  );
}
